import { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  ClipboardDocumentIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import NewSideBar from '../../components/Navigation/NewSideBar';
import SmashLoader from '../../components/Loader/SmashLoader';
import { LayoutContext } from '../../utils/contexts/LayoutContext';
import { useDebounce } from '../../utils/hooks/useDebounce';
import {
  deleteOneSession,
  fetchSessionAdminSummary,
  fetchSessions,
} from '../../services/Interviews/sessionService';

const PAGE_SIZE = 15;
const statusLabels = {
  in_progress: 'In progress', submitted: 'Submitted', generating: 'Generating evaluation',
  awaiting_review: 'Awaiting expert review', approved: 'Approved', failed: 'Evaluation failed',
};
const statusStyles = {
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
  submitted: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200',
  generating: 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-200',
  awaiting_review: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  approved: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
};
const formatDate = (value) => value
  ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
  : 'Not completed';

export default function Sessions({ embedded = false }) {
  const { uuid } = useParams();
  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [workflowStatus, setWorkflowStatus] = useState('');
  const [ordering, setOrdering] = useState('-created');
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const setPadding = useContext(LayoutContext)?.setPadding;

  useEffect(() => {
    if (embedded || !setPadding) return undefined;
    setPadding('p-0');
    return () => setPadding('p-4');
  }, [embedded, setPadding]);

  const loadSessions = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    const result = await fetchSessions({
      params: {
        interviewId: uuid,
        search: debouncedSearch || undefined,
        workflowStatus: workflowStatus || undefined,
        ordering,
        page,
        pageSize: PAGE_SIZE,
      },
      signal,
    });
    if (signal?.aborted) return;
    if (!result.success) {
      setError(result.error?.response?.data?.detail || 'Student sessions could not be loaded.');
      setSessions([]);
    } else {
      setSessions(result.data?.results || []);
      setCount(result.data?.count || 0);
    }
    setLoading(false);
  }, [debouncedSearch, ordering, page, uuid, workflowStatus]);

  const loadSummary = useCallback((signal) => fetchSessionAdminSummary({ interviewId: uuid, signal })
    .then(setSummary)
    .catch(() => setSummary(null)), [uuid]);

  useEffect(() => {
    const controller = new AbortController();
    loadSessions(controller.signal);
    return () => controller.abort();
  }, [loadSessions]);
  useEffect(() => {
    const controller = new AbortController();
    loadSummary(controller.signal);
    return () => controller.abort();
  }, [loadSummary]);
  useEffect(() => setPage(1), [debouncedSearch, ordering, workflowStatus]);

  const copySessionLink = async (sessionId) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/interviews/session/${sessionId}`);
      toast.success('Student session link copied.');
    } catch (copyError) {
      toast.error('The session link could not be copied.');
    }
  };

  const removeSession = async (session) => {
    const studentName = `${session.person?.first_name || ''} ${session.person?.last_name || ''}`.trim() || session.person?.email;
    if (!window.confirm(`Remove the interview session for ${studentName}? This can remove associated responses and evaluation records.`)) return;
    try {
      await deleteOneSession({ params: { sessionId: session.uuid } });
      toast.success('Session removed.');
      await Promise.all([loadSessions(), loadSummary()]);
    } catch (requestError) {
      toast.error(requestError?.response?.data?.detail || 'The session could not be removed.');
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const inputClass = 'mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 dark:border-gray-600 dark:bg-gray-900';
  const summaryCards = summary ? [
    ['Total participants', summary.total], ['In progress', summary.in_progress],
    ['Awaiting review', summary.awaiting_review], ['Approved', summary.approved],
  ] : [];

  return (
    <div className={`flex h-full ${embedded ? '' : 'max-h-main'}`}>
      {!embedded && <NewSideBar />}
      <main className={`w-full overflow-y-auto ${embedded ? '' : 'p-6 lg:p-8'}`}>
        <div className="mx-auto max-w-7xl space-y-6">
          <header className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div><h2 className="text-2xl font-bold">Sessions and participants</h2><p className="mt-1 text-sm text-gray-500">Assign students, share interview access, and follow every evaluation through approval.</p></div>
            <Link to={`/organization/interviews/${uuid}/sessions/new`} className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"><PlusIcon className="h-5 w-5" />Create session</Link>
          </header>

          {summaryCards.length > 0 && <section aria-label="Session summary" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{summaryCards.map(([label, value]) => <article key={label} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"><strong className="text-3xl">{value}</strong><p className="mt-1 text-sm text-gray-500">{label}</p></article>)}</section>}

          <section aria-label="Session filters" className="grid gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 md:grid-cols-3">
            <label className="text-sm font-medium">Search students<input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name or email" className={inputClass} /></label>
            <label className="text-sm font-medium">Workflow status<select value={workflowStatus} onChange={(event) => setWorkflowStatus(event.target.value)} className={inputClass}><option value="">All statuses</option>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label className="text-sm font-medium">Sort by<select value={ordering} onChange={(event) => setOrdering(event.target.value)} className={inputClass}><option value="-created">Newest assigned</option><option value="created">Oldest assigned</option><option value="-end_datetime">Recently completed</option><option value="end_datetime">Earliest completed</option></select></label>
          </section>

          {error && <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-800"><p>{error}</p><button type="button" onClick={() => loadSessions()} className="mt-2 font-semibold underline">Try again</button></div>}
          {loading ? <div className="flex min-h-64 items-center justify-center"><SmashLoader /></div> : sessions.length ? (
            <section aria-label="Student sessions" className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="overflow-x-auto"><table className="w-full min-w-[850px] text-left text-sm"><thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900"><tr><th className="px-5 py-4">Student</th><th className="px-5 py-4">Status</th><th className="px-5 py-4">AI presenter</th><th className="px-5 py-4">Assigned</th><th className="px-5 py-4">Completed</th><th className="px-5 py-4 text-right">Actions</th></tr></thead><tbody className="divide-y divide-gray-200 dark:divide-gray-700">{sessions.map((session) => {
                const person = session.person || {};
                const fullName = [person.first_name, person.middle_name, person.last_name].filter(Boolean).join(' ') || 'Unnamed student';
                const status = session.workflow_status || (session.end_datetime ? 'submitted' : 'in_progress');
                return <tr key={session.uuid} className="hover:bg-gray-50 dark:hover:bg-gray-900/60"><td className="px-5 py-4"><Link to={`/portfolio/${person.uuid}`} className="font-semibold text-gray-950 hover:text-blue-600 dark:text-white">{fullName}</Link><p className="mt-1 text-xs text-gray-500">{person.email}</p></td><td className="px-5 py-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusStyles[status] || 'bg-gray-100 text-gray-700'}`}>{statusLabels[status] || status}</span></td><td className="px-5 py-4"><span className="font-medium">{session.agent?.name || 'Not assigned'}</span></td><td className="px-5 py-4 text-gray-500">{formatDate(session.created)}</td><td className="px-5 py-4 text-gray-500">{formatDate(session.end_datetime)}</td><td className="px-5 py-4"><div className="flex justify-end gap-2"><button type="button" onClick={() => copySessionLink(session.uuid)} title="Copy student session link" aria-label={`Copy session link for ${fullName}`} className="rounded-lg border p-2 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"><ClipboardDocumentIcon className="h-4 w-4" /></button>{session.end_datetime && <Link to={`/organization/interviews/${uuid}/reports/${session.uuid}`} className="rounded-lg border px-3 py-2 font-semibold hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700">Responses</Link>}{session.review_uuid && <Link to={`/organization/reviews/${session.review_uuid}`} className="rounded-lg bg-blue-600 px-3 py-2 font-semibold text-white hover:bg-blue-700">Review</Link>}<button type="button" onClick={() => removeSession(session)} title="Remove session" aria-label={`Remove session for ${fullName}`} className="rounded-lg border border-red-200 p-2 text-red-600 hover:bg-red-50 dark:border-red-800"><TrashIcon className="h-4 w-4" /></button></div></td></tr>;
              })}</tbody></table></div>
            </section>
          ) : !error && <section className="rounded-2xl border border-dashed border-gray-300 bg-white p-12 text-center dark:border-gray-700 dark:bg-gray-800"><h3 className="text-lg font-bold">No sessions found</h3><p className="mt-2 text-sm text-gray-500">{search || workflowStatus ? 'Adjust the filters to see other participants.' : 'Create the first session to assign a student to this interview.'}</p>{!search && !workflowStatus && <Link to={`/organization/interviews/${uuid}/sessions/new`} className="mt-5 inline-flex rounded-xl bg-blue-600 px-4 py-2.5 font-semibold text-white">Create session</Link>}</section>}

          {!loading && !error && count > PAGE_SIZE && <nav aria-label="Session pagination" className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"><button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded-lg border px-4 py-2 disabled:opacity-40">Previous</button><span className="text-sm">Page {page} of {totalPages} · {count} sessions</span><button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)} className="rounded-lg border px-4 py-2 disabled:opacity-40">Next</button></nav>}
          <button type="button" onClick={() => { loadSessions(); loadSummary(); }} className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-blue-600"><ArrowPathIcon className="h-4 w-4" />Refresh sessions</button>
        </div>
      </main>
    </div>
  );
}
